import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { CustomerAddress, ShippingInfo } from '@/types'

/**
 * The customer address book and delivery info, shared by checkout and any
 * future account page. Address edits invalidate the shipping quote too: a
 * changed state can move the parcel to the other zone.
 */

export interface AddressForm {
  label?: string | null
  recipient_name: string
  phone: string
  line1: string
  line2?: string | null
  city: string
  postcode: string
  state: string
  is_default?: boolean
}

export function useAddresses() {
  return useQuery({
    queryKey: ['addresses'],
    queryFn: async () => (await api.get<{ data: CustomerAddress[] }>('/addresses')).data.data,
  })
}

/** State list and pickup card details - reference data, cached for the session. */
export function useShippingInfo() {
  return useQuery({
    queryKey: ['shipping-info'],
    queryFn: async () => (await api.get<ShippingInfo>('/shipping/info')).data,
    staleTime: Infinity,
  })
}

function useInvalidateAddresses() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({ queryKey: ['addresses'] })
    queryClient.invalidateQueries({ queryKey: ['shipping-quote'] })
  }
}

export function useSaveAddress() {
  const invalidate = useInvalidateAddresses()

  return useMutation({
    mutationFn: async ({ id, form }: { id?: number; form: AddressForm }) => {
      const response = id
        ? await api.patch<{ data: CustomerAddress }>(`/addresses/${id}`, form)
        : await api.post<{ data: CustomerAddress }>('/addresses', form)

      return response.data.data
    },
    onSuccess: invalidate,
  })
}

export function useDeleteAddress() {
  const invalidate = useInvalidateAddresses()

  return useMutation({
    mutationFn: async (id: number) => (await api.delete(`/addresses/${id}`)).data,
    onSuccess: invalidate,
  })
}

export function useSetDefaultAddress() {
  const invalidate = useInvalidateAddresses()

  return useMutation({
    mutationFn: async (id: number) => (await api.post(`/addresses/${id}/default`)).data,
    onSuccess: invalidate,
  })
}
